// covered-by: frontends/carrie/src/ask-v2/review/__tests__/reviewCommentTags.test.ts
import { type AskPunttReviewComment } from "@mg/schemas/src/christo/askPunttReview";

/** Wire tag ids emitted on review comments (schema lands separately). */
export const REVIEW_COMMENT_TAG_IDS = [
  "nutrition",
  "completeness",
  "copy-diff",
  "code-values",
  "variants",
  "regulated-copy",
] as const;

export type ReviewCommentTagId = (typeof REVIEW_COMMENT_TAG_IDS)[number];

export type CommentWithOptionalTags = AskPunttReviewComment & {
  tags?: readonly string[];
};

const TAG_ALIAS: Record<string, ReviewCommentTagId> = {
  nutrition: "nutrition",
  completeness: "completeness",
  "copy-diff": "copy-diff",
  copy_diff: "copy-diff",
  "code-values": "code-values",
  code_values: "code-values",
  codes_values: "code-values",
  variants: "variants",
  "regulated-copy": "regulated-copy",
  regulated_copy: "regulated-copy",
};

/** i18n keys under askV2.review.commentTags.* */
export function reviewCommentTagLabelKey(
  tag: ReviewCommentTagId,
): `askV2.review.commentTags.${ReviewCommentTagId}` {
  return `askV2.review.commentTags.${tag}`;
}

export function normalizeReviewCommentTag(
  value: string,
): ReviewCommentTagId | null {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;
  return TAG_ALIAS[trimmed] ?? null;
}

export function reviewCommentTags(
  comment: CommentWithOptionalTags,
): ReviewCommentTagId[] {
  const raw = comment.tags ?? [];
  const normalized = raw
    .map((tag) => normalizeReviewCommentTag(tag))
    .filter((tag): tag is ReviewCommentTagId => tag != null);
  return Array.from(new Set(normalized));
}

export function commentMatchesActiveTags(
  comment: CommentWithOptionalTags,
  activeTags: ReadonlySet<ReviewCommentTagId>,
): boolean {
  const tags = reviewCommentTags(comment);
  if (tags.length === 0) return true;
  return tags.some((tag) => activeTags.has(tag));
}

export function isReviewCommentVisible(
  comment: CommentWithOptionalTags,
  activeTags: ReadonlySet<ReviewCommentTagId>,
): boolean {
  if (comment.isDeleted) return false;
  return commentMatchesActiveTags(comment, activeTags);
}

export function defaultActiveReviewCommentTags(): Set<ReviewCommentTagId> {
  return new Set(REVIEW_COMMENT_TAG_IDS);
}
